import { ExportOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import { Button, Form } from 'antd';
import React, { useContext, useState } from 'react';
import { AppContext } from '../../../../../AppContext';
import {
  EXPORT_TYPES,
  PERMISSIONS_KEY,
  PERMISSION_TYPE,
  ROUTES,
} from '../../../../../common/constants';
import { hasPermission } from '../../../../../common/utils';
import { isUnitsFeatureEnabledForLibrary } from '../../../../../hooks/useInventoryUnitsFeature';
import ExportDateRangeModal from '../../../../../components/ExportDateRangeModal';
import GoBackButton from '../../../../../components/GoBackButton';
import DownloadQrCodesButton from '../../../../../components/InventoryUnits/DownloadQrCodesButton';
import SyncQrCodesButton from '../../../../../components/InventoryUnits/SyncQrCodesButton';
import Portal from '../../../../../components/Portal';
import InventoryTable from '../components/InventoryTable';
import LibraryTabs from '../components/LibraryTabs';
import { REQUEST_LIBRARY_INVENTORY_EXPORT_ADMIN } from '../graphql/Mutations';

const InventoryDetails = ({
  match: { params: { id, libraryNumber } = {} } = {},
}) => {
  const {
    state: { permissions },
  } = useContext(AppContext);
  const [form] = Form.useForm();
  const [isAddToyModalOpen, setIsAddToyModalOpen] = useState(false);
  const [exportFilter, setExportFilter] = useState();
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedInventoryIds, setSelectedInventoryIds] = useState([]);
  const [selectionResetKey, setSelectionResetKey] = useState(0);

  const renderUnitActionButtons = () =>
    isUnitsFeatureEnabledForLibrary(id) && (
      <>
        {hasPermission(
          PERMISSIONS_KEY?.MASTER_LIST_LIBRARY_INVENTORY_DETAILS,
          PERMISSION_TYPE?.UPDATE,
          permissions,
        ) && (
          <SyncQrCodesButton
            className="common-button mr-16"
            size="middle"
            libraryId={id}
            selectedInventoryIds={selectedInventoryIds}
            onSuccess={() => setSelectionResetKey((prev) => prev + 1)}
          />
        )}
        <DownloadQrCodesButton
          className="common-button mr-16"
          size="middle"
          libraryId={id}
          selectedInventoryIds={selectedInventoryIds}
        />
      </>
    );

  const [requestLibraryInventoryExportAdmin] = useMutation(
    REQUEST_LIBRARY_INVENTORY_EXPORT_ADMIN,
    {
      onError: () => {},
    },
  );

  const handleExport = async (values) => {
    setIsExportLoading(true);
    const res = await requestLibraryInventoryExportAdmin({
      variables: {
        where: {
          id,
        },
        data: {
          type: EXPORT_TYPES?.LIBRARY_INVENTORY,
        },
        filter: {
          search: exportFilter?.search,
          brandIds: exportFilter?.brand,
          categoryIds: exportFilter?.category,
          materialIds: exportFilter?.material,
          ageGroupIds: exportFilter?.ageGroup,
          dateRange: {
            from: values?.date?.[0]?.startOf('day'),
            to: values?.date?.[1]?.endOf('day'),
          },
        },
      },
    });
    if (res?.data) {
      setIsModalOpen(false);
      form?.resetFields();
    }
    setIsExportLoading(false);
  };

  return (
    <>
      <Portal portalId="header-title-content">
        <GoBackButton
          customLink={`${ROUTES?.MASTER_LISTS}${ROUTES?.LIBRARY}`}
        />
        <div className="portal-header">Edit Library</div>
      </Portal>
      <Portal portalId="header-right-content">
        {renderUnitActionButtons()}
        {hasPermission(
          PERMISSIONS_KEY?.MASTER_LIST_LIBRARY_INVENTORY_DETAILS,
          PERMISSION_TYPE?.CREATE,
          permissions,
        ) && (
          <Button
            className="common-button"
            size="middle"
            icon={<PlusOutlined />}
            onClick={() => setIsAddToyModalOpen(true)}
          >
            Add Toys
          </Button>
        )}
        <Button
          className="common-button ml-16"
          size="middle"
          icon={<ExportOutlined />}
          onClick={() => setIsModalOpen(true)}
          loading={isExportLoading}
        >
          Export
        </Button>
      </Portal>
      <Portal portalId="header-right-content-phones">
        {renderUnitActionButtons()}
        {hasPermission(
          PERMISSIONS_KEY?.MASTER_LIST_LIBRARY_INVENTORY_DETAILS,
          PERMISSION_TYPE?.CREATE,
          permissions,
        ) && (
          <Button
            className="common-button"
            size="middle"
            icon={<PlusOutlined />}
            onClick={() => setIsAddToyModalOpen(true)}
          >
            Add Toys
          </Button>
        )}
        <Button
          className="common-button ml-16"
          size="middle"
          icon={<ExportOutlined />}
          onClick={() => setIsModalOpen(true)}
          loading={isExportLoading}
        >
          Export
        </Button>
      </Portal>
      <ExportDateRangeModal
        form={form}
        onFinish={handleExport}
        loadings={isExportLoading}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
      />
      <LibraryTabs libraryId={id} libraryNumber={libraryNumber}>
        <div className="full-height-without-details">
          <InventoryTable
            libraryId={id}
            libraryNumber={libraryNumber}
            isAddToyModalOpen={isAddToyModalOpen}
            setIsAddToyModalOpen={setIsAddToyModalOpen}
            exportFilter={exportFilter}
            setExportFilter={setExportFilter}
            handleExport={handleExport}
            isExportLoading={isExportLoading}
            onSelectedInventoryIdsChange={setSelectedInventoryIds}
            selectionResetKey={selectionResetKey}
          />
        </div>
      </LibraryTabs>
    </>
  );
};

export default InventoryDetails;
